// The web app as a host of the chat SDK's views (ADR 0021): the SAME @cortex/sdk-chat-react views a
// project's standalone app mounts, given the SDK's client on this origin and cortex's own affordances as
// slots — the viz sandbox (VizView), the floating chart windows, the project page routes, the query and
// Board refresh after a tool, the agents roster, the fleet catalog's model names, live dashboards (the
// reports module). Over an open app the chat draws nothing (companion UI is off for every app, owner 2026-11-04).
import { createChatClient } from '@cortex/sdk-chat-client';
import { ChatPanel, ChatProvider, ChatReportPage, type ChatHost } from '@cortex/sdk-chat-react';
import * as React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { APP_REACH_HEADER, api, getAccessToken } from '../platform/index.ts';
import { openChartWindow } from '@cortex/app-shell';
import { chatPagePath, keysChangedBy } from './chat/effects.ts';
import { config } from '../platform/index.ts';
import { projectPath } from '../platform/index.ts';
import { useAuth } from '../identity/index.ts';
import { BOARD_CHANGED_EVENT } from '../identity/index.ts';
import { invalidate } from '../platform/index.ts';
import { useUiView } from '../platform/index.ts';
import { VizView } from '@cortex/app-shell';
import { OwnViews } from '../platform/index.ts';
import { generateDashboard } from './chat-dashboard.ts';
import { reportPathOf } from '../mini-apps/index.ts';

/** A project's chat on the api (`/api/assistant/projects/<slug>`); inside an app, every call names it. */
const chatClient = (slug: string, app?: string) =>
  createChatClient({
    baseUrl: `${new URL(config.VITE_API_BASE_URL, window.location.origin).toString().replace(/\/$/, '')}/api/assistant/projects/${encodeURIComponent(slug)}`,
    token: async () => getAccessToken() ?? null,
    // Inside an app, every call names it: a person who reaches the project only through the app (an app
    // user, an app share) keeps their own chat there; the api decides.
    ...(app
      ? {
          fetch: (input: RequestInfo | URL, init?: RequestInit) => {
            const req = new Request(input, init);
            req.headers.set(APP_REACH_HEADER, app);
            return fetch(req);
          },
        }
      : {}),
  });
/** One client per project and app (the views ask for theirs on every render). */
const clients = new Map<string, ReturnType<typeof chatClient>>();
const clientOf = (slug: string, app?: string) => {
  const k = `${slug}\n${app ?? ''}`;
  let c = clients.get(k);
  if (!c) clients.set(k, (c = chatClient(slug, app)));
  return c;
};

type BranchHost = Omit<ChatHost, 'navigate' | 'simple' | 'userEmail' | 'clientOf'>;

/**
 * Cortex's slots of the chat views for the preview of one branch (the chat names no branch, ADR 0058 §7.7): the
 * turn's `branch` field, the header's branch, the project pages and app routes of the branch, its agents, its
 * Requirements source, its reports and its apps' companions. One per branch, so the views' readers stay stable.
 */
const hosts = new Map<string, BranchHost>();
function hostOf(branch: string): BranchHost {
  let h = hosts.get(branch);
  if (!h) hosts.set(branch, (h = branchHost(branch)));
  return h;
}

const branchHost = (branch: string): BranchHost => ({
  assistantBaseUrl: config.VITE_ASSISTANT_BASE_URL,
  turnFields: { branch },
  context: branch,
  // Read at each call (the session's token changes; the api module holds the current one).
  getToken: () => getAccessToken(),
  renderViz: (p) => (
    <VizView
      bare
      testid={p.testid}
      title={p.title}
      code={p.code}
      data={p.data}
      {...(p.height !== undefined ? { height: p.height } : {})}
      {...(p.onFix ? { onFix: p.onFix } : {})}
    />
  ),
  openChartWindow,
  pagePath: (raw, slug) => {
    const p = chatPagePath(raw);
    return p ? projectPath(p, slug, branch) : null;
  },
  appPath: (app, slug) => projectPath(`apps/${encodeURIComponent(app)}`, slug, branch),
  onToolDone: (tool, slug) => {
    const keys = keysChangedBy(tool, slug, branch);
    invalidate(keys);
    if (keys.some((k) => k[0] === 'board')) window.dispatchEvent(new Event(BOARD_CHANGED_EVENT));
  },
  listAgents: async (slug) => {
    const { data } = await api.GET('/api/agents/projects/{slug}/branches/{branch}/agents', {
      params: { path: { slug, branch } },
    });
    return data?.agents ?? [];
  },
  // The fleet's models, named as cortex's fleet names them.
  modelLabels: async () => {
    const { data } = await api.GET('/api/fleet/models');
    const catalog = (data as { catalog?: { id: string; label: string }[] } | undefined)?.catalog ?? [];
    return new Map(catalog.map((c) => [c.id, c.label] as [string, string]));
  },
  generateDashboard: (slug, r) => generateDashboard(slug, branch, r),
  // The feature checklist's selection: a change request, recorded as a Requirements source of the branch.
  recordFeatures: async (slug, r) => {
    const res = await api.POST('/api/assistant/projects/{slug}/feature-selections', {
      params: { path: { slug } },
      body: { branch, ...r },
    });
    if (!res.response.ok) throw new Error(String(res.response.status));
  },
  reportPath: (slug, id) => reportPathOf(slug, branch, id),
  brand: 'Cortex',
});

/**
 * The chat views' provider with cortex's slots (the person, the router, the Standard/Advanced view). Inside
 * an app (`app`), the chat's calls name it.
 */
export function CortexChatProvider({
  children,
  app,
  branch,
  Provider = ChatProvider,
}: {
  children: React.ReactNode;
  app?: string;
  /** The chat's provider (a branch's own copy's on its report page). */
  Provider?: typeof ChatProvider;
  /** The branch whose preview the chat is for. */
  branch: string;
}) {
  const navigate = useNavigate();
  const { simple } = useUiView();
  const { state: auth } = useAuth();
  const email = auth.status === 'signed-in' ? auth.user.email : null;
  // The fleet's catalog is the platform admins' (others get a 403): anyone else sees the model ids as named.
  const platformAdmin = auth.status === 'signed-in' && !!auth.capabilities?.platformAdmin;
  const value = React.useMemo<ChatHost>(
    () => ({
      ...hostOf(branch),
      clientOf: (slug: string) => clientOf(slug, app),
      navigate: (path) => void navigate(path),
      simple,
      userEmail: email,
      ...(platformAdmin ? {} : { modelLabels: async () => new Map<string, string>() }),
    }),
    [navigate, simple, email, app, branch, platformAdmin],
  );
  return <Provider value={value}>{children}</Provider>;
}

/** The chat window of a project branch (the orchestrator, or the Pipeline engine window), or of an app. */
export function CortexChatPanel({
  branch,
  ...p
}: React.ComponentProps<typeof ChatPanel> & { branch: string }) {
  return (
    <CortexChatProvider branch={branch} {...(p.app ? { app: p.app } : {})}>
      <ChatPanel {...p} />
    </CortexChatProvider>
  );
}

/** A saved chat report on its own page (the chat's "Open ↗" popup, the rail). */
export function ChatReportRoute() {
  const { slug = '', branch = 'main' } = useParams();
  const [params] = useSearchParams();
  return (
    <OwnViews module="chat" slug={slug} branch={branch} cortex={{ ChatProvider, ChatReportPage }}>
      {(v, key) => (
        <CortexChatProvider key={key} branch={branch} Provider={v.ChatProvider}>
          <v.ChatReportPage slug={slug} id={params.get('id') ?? ''} />
        </CortexChatProvider>
      )}
    </OwnViews>
  );
}
